import { useEffect, useState } from "react";
import { ArrowLeft, Menu, X } from "lucide-react";
import { LOGO } from "@/data/logoData";

const LINKS = [
  { id: "solutions", label: "Solutions" },
  { id: "projects", label: "Projects" },
  { id: "why-us", label: "Why Us" },
  { id: "about", label: "About" },
  { id: "locations", label: "Locations" },
  { id: "contact", label: "Contact" },
];

export default function Navbar({ divisionLabel, onHome }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    setOpen(false);
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -72 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="portal-navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled ? "glass-strong shadow-2xl shadow-black/40" : "bg-transparent"
      }`}
      aria-label="Portal navigation"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-back-home"
          onClick={onHome}
          className="group flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-slate-600/80 px-4 py-2 text-xs font-semibold text-slate-200 transition-colors duration-300 hover:border-brand hover:bg-brand hover:text-white"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to Home
        </button>
        <span className="hidden shrink-0 rounded-2xl bg-white px-4 py-2.5 md:flex xl:px-5 xl:py-3" data-testid="nav-logo">
          <img src={LOGO} alt="Bigboss Digitech — ELV and smart automation company logo" width="1280" height="560" className="h-10 w-auto xl:h-12" />
        </span>

        <div className="hidden min-w-0 items-center gap-0.5 lg:flex xl:gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => jump(l.id)}
              className="whitespace-nowrap rounded-full px-3 py-2 text-xs font-medium xl:px-4 text-slate-300 transition-colors duration-300 hover:bg-white/5 hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className="hidden whitespace-nowrap rounded-full bg-brand/15 px-4 py-1.5 font-tech text-[10px] uppercase tracking-[0.25em] text-brand sm:block" data-testid="nav-division-badge">
            {divisionLabel} Division
          </span>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-600 text-slate-200 lg:hidden"
            aria-label="Toggle navigation menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass-strong mx-4 mb-4 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-2xl p-2 sm:mx-6 lg:hidden" data-lenis-prevent data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}-link`}
              onClick={() => jump(l.id)}
              className="block w-full rounded-xl px-4 py-3 text-left text-sm text-slate-200 transition-colors hover:bg-white/5"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
